"use client";

import cn from "clsx";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { parseAsFloat, useQueryStates } from "nuqs";
import { useMemo } from "react";
import {
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiSkipLeftLine,
  RiSkipRightLine,
} from "react-icons/ri";

interface PaginationProps {
  total: number;
  mode?: "seo" | "manual";
  defaultOptions?: { page: number; perPageRange: number[] };
  scrollOnPageClick?: boolean;
  onChange?: (page: number, limit: number) => void;
  preserveQueryKeys?: string[];
}

export const Pagination = ({
  total,
  mode = "seo",
  defaultOptions = {
    page: 1,
    perPageRange: [10, 20, 50],
  },
  onChange,
  scrollOnPageClick = true,
}: PaginationProps) => {
  const t = useTranslations("common");
  const pathname = usePathname();
  const [{ page: currentPage, limit: perPage }] = useQueryStates({
    page: parseAsFloat.withDefault(defaultOptions.page),
    limit: parseAsFloat.withDefault(defaultOptions.perPageRange[0]),
  });

  const searchParams = useSearchParams();
  const extraSearchParams = useMemo(() => {
    const newSp = new URLSearchParams(searchParams);
    newSp.delete("page");
    newSp.delete("limit");
    return newSp.toString();
  }, [searchParams]);

  const numberOfPages = Math.ceil(total / perPage);
  const skipped = perPage * currentPage;

  if (total <= perPage) return null;

  const onPageChange = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, page: number) => {
    if (page <= 0 || page > numberOfPages) return;
    if (mode === "manual") {
      e.preventDefault();
      onChange && onChange(page, perPage);
    }
  };

  return (
    <div className="relative my-8 flex items-center justify-center">
      <p className="absolute left-0 mx-2 hidden p-2 lg:block">
        {t("text-pagination-page-summary", {
          firstItemNumber: (currentPage - 1) * perPage + 1,
          lastItemNumber: skipped > total ? total : skipped,
          totalItems: total,
        })}
      </p>
      <div className="flex">
        <Link
          href={`${pathname}?page=${1}&${extraSearchParams}`}
          aria-label={t("text-first")}
          title={t("text-first")}
          scroll={scrollOnPageClick}
          aria-disabled={currentPage === 1}
          className={cn(
            "border-grey-80 text-grey-80 mx-2 flex size-8 items-center justify-center rounded-full border-2 text-2xl",
            currentPage === 1 ? "cursor-not-allowed" : "cursor-pointer",
          )}
          onClick={(e) => onPageChange(e, 1)}
        >
          <RiSkipLeftLine />
        </Link>
        <Link
          href={currentPage !== 1 ? `${pathname}?page=${currentPage - 1}&${extraSearchParams}` : ""}
          aria-label={t("text-previous")}
          title={t("text-previous")}
          scroll={scrollOnPageClick}
          aria-disabled={currentPage === 1}
          className={cn(
            "border-grey-80 text-grey-80 mx-2 flex size-8 items-center justify-center rounded-full border-2 text-2xl",
            currentPage === 1 ? "cursor-not-allowed" : "cursor-pointer",
          )}
          onClick={(e) => currentPage !== 1 && onPageChange(e, currentPage - 1)}
        >
          <RiArrowLeftSLine />
        </Link>
        <span className="mx-6 flex items-center">
          {t("text-pagination-total-pages-info", { currentPage, numberOfPages })}
        </span>
        <Link
          href={
            currentPage !== numberOfPages
              ? `${pathname}?page=${currentPage + 1}&${extraSearchParams}`
              : ""
          }
          aria-label={t("text-next")}
          title={t("text-next")}
          scroll={scrollOnPageClick}
          aria-disabled={currentPage === numberOfPages}
          className={cn(
            "border-grey-80 text-grey-80 mx-2 flex size-8 items-center justify-center rounded-full border-2 text-2xl",
            currentPage === numberOfPages ? "cursor-not-allowed" : "cursor-pointer",
          )}
          onClick={(e) => currentPage !== numberOfPages && onPageChange(e, currentPage + 1)}
        >
          <RiArrowRightSLine />
        </Link>
        <Link
          href={`${pathname}?page=${numberOfPages}&${extraSearchParams}`}
          aria-label={t("text-last")}
          title={t("text-last")}
          scroll={scrollOnPageClick}
          aria-disabled={currentPage === numberOfPages}
          className={cn(
            "border-grey-80 text-grey-80 mx-2 flex size-8 items-center justify-center rounded-full border-2 text-2xl",
            currentPage === numberOfPages ? "cursor-not-allowed" : "cursor-pointer",
          )}
          onClick={(e) => onPageChange(e, numberOfPages)}
        >
          <RiSkipRightLine />
        </Link>
      </div>
    </div>
  );
};
